import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { useScroll } from "framer-motion";
import { trackPageview, observeSections } from "@/lib/analytics";
import { useSiteContent } from "@/context/SiteContent";
import Header from "@/components/landing/Header";
import ScrollProgress from "@/components/landing/ScrollProgress";
import CustomCursor from "@/components/landing/CustomCursor";
import HeroExperience from "@/components/landing/HeroExperience";
import WorldCanvas from "@/components/landing/WorldCanvas";
import MarqueeStrip from "@/components/landing/MarqueeStrip";
import BrandStatement from "@/components/landing/BrandStatement";
import Websites from "@/components/landing/Websites";
import ClothingDesign from "@/components/landing/ClothingDesign";
import BrandAufbau from "@/components/landing/BrandAufbau";
import Portfolio from "@/components/landing/Portfolio";
import About from "@/components/landing/About";
import Partners from "@/components/landing/Partners";
import ContactSection from "@/components/landing/ContactSection";
import { ContactDrawer } from "@/components/landing/ContactDrawer";
import Footer from "@/components/landing/Footer";
import CookieBanner from "@/components/landing/CookieBanner";
import Scene from "@/components/landing/Scene";
import ChapterScene from "@/components/landing/ChapterScene";

/**
 * LANDING — eine durchgehende filmische Scroll-Timeline.
 *
 * Jede Section ist eine gestackte Scene (sticky): sie zieht über die
 * vorherige und löst sich beim Weitergehen in der Tiefe auf. Zwischen den
 * Akten pinnen ChapterScenes. Darunter morpht die WorldCanvas-Partikelwelt
 * synchron zu den echten Sektions-Positionen. Alle Inhalte unverändert.
 */
export default function Landing() {
  const { content } = useSiteContent();
  const s = content.sections || {};
  const show = (key) => s[key] !== false;
  // Fortschritt der „Weltzone" (alles nach dem Hero) — steuert die WebGL-Welt
  const worldRef = useRef(null);
  const { scrollYProgress: worldProgress } = useScroll({
    target: worldRef,
    offset: ["start end", "end end"],
  });
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.11, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    trackPageview("/");
    const sectionObserver = observeSections({
      "hero-experience": "Hero-Experience",
      "brand-statement": "Markenbotschaft",
      "webdesign": "Webdesign",
      "clothing-design": "Clothing Design",
      "clothing-brand": "Clothing Brand Aufbau",
      "portfolio": "Portfolio",
      "about-section": "Über mich",
      "partners-section": "Partner (Rent500 / High End)",
      "contact-section": "Projekt starten",
    });
    return () => {
      cancelAnimationFrame(raf);
      sectionObserver.disconnect();
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <main data-testid="landing-page" className="relative bg-[#050505] text-bone overflow-x-clip">
      <Header />
      <ScrollProgress />
      <CustomCursor />

      {/* Akt 0 — Hero-Experience; klebt am Ende, die Welt zieht darüber.
          (Exit-Treiber = world-zone; der fixed WorldCanvas wird übersprungen) */}
      <Scene name="hero" z={20} enter={false}>
        <HeroExperience />
      </Scene>

      {/* Die durchgehende cineastische 3D-Welt hinter allen folgenden Sections */}
      <WorldCanvas
        progress={worldProgress}
        worldRef={worldRef}
        layoutKey={JSON.stringify(content.sections || {})}
      />

      <div ref={worldRef} className="world-zone relative z-10" style={{ perspective: "1600px" }}>
        <MarqueeStrip />

        {show("brand_statement") && (
          <Scene name="brand-statement" z={2}>
            <BrandStatement />
          </Scene>
        )}

        {show("webdesign") && (
          <Scene name="chapter-webdesign" z={3} enter={false}>
            <ChapterScene nr="01" label="Webdesign" testId="chapter-scene-1" />
          </Scene>
        )}
        {show("webdesign") && (
          <Scene name="webdesign" z={4}>
            <Websites />
          </Scene>
        )}

        {show("clothing_design") && (
          <Scene name="chapter-clothing-design" z={5} enter={false}>
            <ChapterScene nr="02" label="Clothing Design" testId="chapter-scene-2" />
          </Scene>
        )}
        {show("clothing_design") && (
          <Scene name="clothing-design" z={6}>
            <ClothingDesign />
          </Scene>
        )}

        {show("clothing_brand") && (
          <Scene name="chapter-clothing-brand" z={7} enter={false}>
            <ChapterScene nr="03" label="Clothing Brand Aufbau" testId="chapter-scene-3" />
          </Scene>
        )}
        {show("clothing_brand") && (
          <Scene name="clothing-brand" z={8}>
            <BrandAufbau />
          </Scene>
        )}

        {show("portfolio") && (
          <Scene name="chapter-portfolio" z={9} enter={false}>
            <ChapterScene nr="04" label="Portfolio" testId="chapter-scene-4" />
          </Scene>
        )}
        {show("portfolio") && (
          <Scene name="portfolio" z={10}>
            <Portfolio />
          </Scene>
        )}

        {show("ueber_mich") && (
          <Scene name="ueber-mich" z={11}>
            <About />
          </Scene>
        )}

        {show("partner") && (
          <Scene name="partner" z={12}>
            <Partners />
          </Scene>
        )}

        {show("kontakt") && (
          <Scene name="chapter-kontakt" z={13} enter={false}>
            <ChapterScene nr="05" label="Projekt starten" testId="chapter-scene-5" />
          </Scene>
        )}
        {show("kontakt") && (
          <Scene name="kontakt" z={14}>
            <ContactSection />
          </Scene>
        )}

        <Scene name="footer" z={15} last>
          <Footer />
        </Scene>
      </div>
      <CookieBanner />
      <ContactDrawer />
    </main>
  );
}
